import {zodResolver} from '@hookform/resolvers/zod';
import {Typography} from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import React, {Fragment} from 'react';
import {FormProvider, useForm} from 'react-hook-form';

import {AlertModal} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {PasswordWithStrengthValidation} from 'presentation/components/PasswordWithStrengthValidation';
import {useMediaQueries} from 'presentation/pages/private';
import {useForgotPasswordConfirm, useSignUp, useSteps} from 'presentation/pages/public/hooks';
import {StyledFixedFooter} from 'presentation/pages/public/styles';
import {translations} from 'presentation/translations-new';
import {LoginEnum} from 'main/analytics/gaTagsEnum';
import {IFormData, formSchema} from './schema';
import {CreatePasswordStrengthProps} from './types';

const {signIn} = translations['pt-br'].publicFlow.actionText;
const {finalize} = translations['pt-br'].publicFlow.forgotPassword;
const {createPassword, mobileSecurityCodeTitle, modal, confirmModal} =
	translations['pt-br'].privateFlow.profile.changePassword;

export function CreatePasswordStrength({flowType, privateFlow}: CreatePasswordStrengthProps) {
	const {isLaptop} = useMediaQueries();

	const {description, headerTitle, title} =
		translations['pt-br'].publicFlow[flowType].successModal;
	const USE_HOOK = {
		signUp: useSignUp,
		forgotPassword: useForgotPasswordConfirm,
	};

	const methods = useForm<IFormData>({
		defaultValues: {
			password: '',
			repeatPassword: '',
		},
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		delayError: 1000,
	});
	const {
		handleSubmit,
		formState: {isSubmitting, isValid},
	} = methods;

	const {
		apiErrorMsg,
		handleSubmitRequest,
		showAPIError,
		showConfirmationModal,
		confirmationModalState,
		errorModalState,
		setErrorModalState,
	} = USE_HOOK[flowType]({methods, privateFlow});

	const {handleConfirmationModalClose} = useSteps(flowType);

	return (
		<Fragment>
			<FormProvider {...methods}>
				<Stack component="form" gap={2} onSubmit={handleSubmit(handleSubmitRequest)}>
					{privateFlow ? (
						<Stack spacing={2}>
							{isLaptop ? (
								<Typography variant="h6" color="textPrimary">
									{mobileSecurityCodeTitle}
								</Typography>
							) : null}
							<Typography
								variant={isLaptop ? 'subtitle1' : 'h6'}
								color={isLaptop ? 'textSecondary' : 'primary'}>
								{createPassword}
							</Typography>
						</Stack>
					) : null}
					<PasswordWithStrengthValidation flowType={flowType} />
					{showAPIError ? <Alert severity="error">{apiErrorMsg}</Alert> : null}
					<StyledFixedFooter>
						<Button
							data-tag-id={
								privateFlow
									? undefined
									: LoginEnum.CLICK_FINALIZAR_ESQUECI_MINHA_SENHA
							}
							fullWidth
							disabled={isSubmitting || !isValid}
							size="large"
							type="submit"
							endIcon={isSubmitting ? <Loading color="inherit" size={16} /> : null}>
							{finalize}
						</Button>
					</StyledFixedFooter>
				</Stack>
			</FormProvider>
			{showConfirmationModal ? (
				<AlertModal
					variant="success"
					title={title}
					headerTitle={headerTitle}
					description={description}
					open={confirmationModalState}
					onClose={() => handleConfirmationModalClose(Boolean(privateFlow))}
					footerContent={
						<Button
							fullWidth
							size="large"
							onClick={() => handleConfirmationModalClose(Boolean(privateFlow))}>
							{privateFlow ? confirmModal : signIn}
						</Button>
					}
				/>
			) : null}

			{privateFlow ? (
				<AlertModal
					variant="error"
					title={modal.errorTitle}
					headerTitle={mobileSecurityCodeTitle}
					description={modal.errorDescription}
					open={errorModalState}
					onClose={() => setErrorModalState(false)}
					footerContent={
						<Button
							fullWidth
							size="large"
							onClick={() => {
								setErrorModalState(false);
								handleConfirmationModalClose(privateFlow);
							}}>
							{privateFlow ? confirmModal : modal.confirmButton}
						</Button>
					}
				/>
			) : null}
		</Fragment>
	);
}
